import {PropsWithChildren} from "react";
import {StyleSheet, Text, TouchableOpacity, View, Platform} from "react-native";
import {ArrowLeft, RefreshCw} from "lucide-react-native";
import {ThemeType} from "@/theming/baseTheme";
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {useSafeAreaInsets} from "react-native-safe-area-context";
import {useInvalidator} from "@/data/app-data-hooks";

interface HeaderProps extends PropsWithChildren {
    title: string;
    onBackPress: () => void;
    onRightPress?: () => void;
    rightIcon?: React.ReactNode;
    /**
     * Extra controls for the right-hand side, for screens that need more than the one
     * `rightIcon`/`onRightPress` pair can express.
     *
     * `ReactNode` rather than `ReactNode[]`: it already admits a single element, an array or a
     * fragment, so a screen with one action does not have to wrap it in brackets and invent a key
     * for it. Each node renders as-is — the container is already a row with a gap, so an action
     * brings its own touchable and icon and this component does not dictate what one looks like.
     */
    rightActions?: React.ReactNode;
}

export const HeaderText = ({title, onBackPress, rightIcon, onRightPress, rightActions}: HeaderProps) => {
    const {theme} = useCurrentTheme();
    const insets = useSafeAreaInsets();
    const styles = createStyles(theme, insets);
    const {invalidateAppData} = useInvalidator();

    return (
        <View style={styles.header}>
            <TouchableOpacity
                style={styles.backButton}
                onPress={onBackPress}
                activeOpacity={0.7}
            >
                <ArrowLeft style={styles.arrowLeft} />
            </TouchableOpacity>
            <Text style={styles.headerTitle} numberOfLines={1}>{title}</Text>
            <View style={styles.headerRight}>
                {/* Before rightIcon, so adding actions never displaces the control a screen already
                    has: an existing header's icon keeps its position next to the web refresh
                    button and only gains neighbours to its left. */}
                {rightActions}
                {rightIcon && onRightPress &&
                    <TouchableOpacity activeOpacity={0.7} style={styles.backButton} onPress={onRightPress}>
                        {rightIcon}
                    </TouchableOpacity>
                }
                {Platform.OS === 'web' &&
                <TouchableOpacity style={styles.backButton} activeOpacity={0.7} onPress={invalidateAppData}>
                    <RefreshCw/>
                </TouchableOpacity>}
            </View>
        </View>
    );
};

const createStyles = (theme: ThemeType, insets: any) => StyleSheet.create({
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.surface,
        paddingTop: Platform.OS === 'web' ? theme.spacing.md : insets.top,
        paddingHorizontal: theme.spacing.md,
        paddingBottom: theme.spacing.sm,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
    },
    backButton: {
        padding: theme.spacing.xs,
        marginRight: theme.spacing.sm,
        borderRadius: theme.borderRadius.sm,
        color: theme.colors.primary,
        backgroundColor: 'transparent',
        justifyContent: 'center',
        alignItems: 'center',
    },
    headerTitle: {
        flex: 1,
        fontSize: theme.fontSize.lg,
        fontWeight: '600',
        color: theme.colors.text,
        textAlign: 'center',
        marginRight: theme.spacing.sm,
    },
    headerRight: {
        flexDirection: 'row',
        gap: 4
    },
    arrowLeft: {
        color: theme.colors.primary
    }
});
